import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ASSIGNMENT_STATUSES,
  ASSIGNMENT_TYPES,
  ORGANIZATION_USER_ROLE_LABELS,
} from "utils/constants";
import { slugGenerator } from "utils/helpers";
import {
  useCreateTemplate,
  useDeleteTemplate,
  useGetAssignments,
  useGetPatients,
  useGetTemplates,
  useGetGroups,
  useCurrentUser,
} from "utils/hooks";
import { v4 as uuid } from "uuid";
import CurrentAssignmentContext from "./CurrentAssignmentContext";
import { getDefaultNotificationTime } from "utils/helpers";

const useAssignmentLibraryUtils = () => {
  const { uuid: slug } = useParams();
  const hasPatientSlug = slug;

  const { patient, patients } = useGetPatients({
    slug: slug,
  });

  const { specificGroup: group } = useGetGroups({ slug });

  const [currentPatient, setCurrentPatient] = useState(patient);
  const [currentGroup, setCurrentGroup] = useState(group);

  useEffect(() => {
    if (currentPatient !== patient && patient) {
      setCurrentPatient(patient);
    }
    if (currentGroup !== group && group) {
      setCurrentGroup(group);
    }
  }, [currentGroup, currentPatient, group, patient]);

  const { assignments: patientAssignments } = useGetAssignments({
    patientId: currentPatient?.uuid,
  });

  const { groupAssignments } = useGetAssignments({
    groupId: currentGroup?.uuid,
  });

  const { user } = useCurrentUser();

  const assignments = currentGroup ? groupAssignments : patientAssignments;

  const defaultNotification = {
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    notificationTime: [getDefaultNotificationTime()],
    startDate: new Date(),
    endDate: null,
    status: "LIVE",
    notificationMessage: "Don't forget to do your assignment :)",
  };

  const [showAddAssignment, setShowAddAssignment] = useState(false);

  const { currentAssignment, setCurrentAssignment } = useContext(
    CurrentAssignmentContext
  );

  const [currentNotification, setCurrentNotification] =
    useState(defaultNotification);

  const [assignChecked, setAssignChecked] = useState(
    currentAssignment?.default
  );

  const [shareWithOrg, setShareWithOrg] = useState(
    currentAssignment?.shareWithOrganization
  );

  useEffect(() => {
    if (currentAssignment) {
      setAssignChecked(currentAssignment.default);
      setShareWithOrg(currentAssignment.shareWithOrganization);
    }
  }, [currentAssignment]);

  const handleAssignmentButtonClick = (type: any) => {
    setCurrentAssignment(type);
    setShowAddAssignment(true);
  };

  const handleNotificationChange = (notificationValues: any) => {
    const newValues = {
      ...defaultNotification,
      ...notificationValues,
    };

    setCurrentNotification(newValues);
  };

  const availableAssignments = ASSIGNMENT_TYPES.filter(
    (assignment) => assignment.type !== "SAFETY_PLAN"
  );

  const assignmentDetailTabs = [
    {
      name: "Preview",
    },
    {
      name: "Settings",
    },
  ];
  const [activeTab, setActiveTab] = useState(assignmentDetailTabs[0]);

  const handleTabChange = (tab: any) => {
    setActiveTab(tab);
  };

  const activeAssignments = (assignments || []).filter(
    ({ status }: any) => status === ASSIGNMENT_STATUSES.VISIBLE
  );

  const patientHasAssignment = (type: any) =>
    activeAssignments?.some((assignment: any) => assignment.type === type);

  const { builderDrafts } = useGetTemplates({});
  const { submitCreateTemplate } = useCreateTemplate();
  const { submitDeleteTemplate } = useDeleteTemplate();

  const handleStartNewAssignment = async () => {
    const slug = await slugGenerator({ string: "cstmasngmnt" });
    const draftUUID = uuid();
    const newDraft = {
      uuid: draftUUID,
      title: `Untitled Assignment ${
        builderDrafts?.length > 0 ? builderDrafts?.length + 1 : ""
      }`,
      description: null,
      instructions: null,
      sources: null,
      status: "DRAFT",
      category: "WORKSHEET",
      assessmentScoring: {
        isScoringEnabled: false,
        scoringInstructions: "",
        scoringType: "Average",
        isFieldsetScoringEnabled: false,
        fieldSets: [],
        scoreDisplays: [],
        scoreSuffix: "",
        scoreAsPercentage: false,
      },
      slug: slug,
      icon: {
        name: "Draft",
        src: "DraftIcon",
      },
      color: "#89CFF0",
      content: [
        {
          uuid: `${draftUUID}-page-1`,
          name: "Page 1",
          assignmentId: draftUUID,
          slug: `${slug}-page-1`,
          createdAt: new Date().getTime(),
          type: "NEW_SECTION_1",
          label: "Page 1",
          status: "VISIBLE",
          fields: [
            {
              uuid: uuid(),
              type: "DATE",
              label: "Date",
              status: "VISIBLE",
              order: 1,
              name: "date",
              sectionId: `${draftUUID}-page-1`,
            },
          ],
          theme: "1",
          order: "1",
          description: null,
        },
      ],
    };
    submitCreateTemplate(newDraft);
  };

  const handleDeleteAssignmentTemplate = (assignment: any) => {
    submitDeleteTemplate(assignment);
  };

  const formatPatientToSelect = (patient: any) => {
    return {
      value: patient?.uuid,
      label:
        patient?.firstName === null && patient?.lastName === null
          ? patient?.email
          : `${patient?.firstName} ${patient?.lastName}`,
    };
  };

  const isAssignmentOwner = user?.id === currentAssignment?.authorId;

  const limitedAccessRole =
    user?.role === ORGANIZATION_USER_ROLE_LABELS.CLINICIAN ||
    user?.role === ORGANIZATION_USER_ROLE_LABELS.INTERN;

  return {
    defaultNotification,
    handleAssignmentButtonClick,
    showAddAssignment,
    currentAssignment,
    setShowAddAssignment,
    setCurrentAssignment,
    availableAssignments,
    currentNotification,
    handleNotificationChange,
    activeTab,
    handleTabChange,
    assignmentDetailTabs,
    patient,
    patientHasAssignment,
    slug,
    handleStartNewAssignment,
    handleDeleteAssignmentTemplate,
    currentPatient,
    setCurrentPatient,
    formatPatientToSelect,
    patients,
    hasPatientSlug,
    group,
    currentGroup,
    assignChecked,
    setAssignChecked,
    user,
    shareWithOrg,
    setShareWithOrg,
    isAssignmentOwner,
    limitedAccessRole,
  };
};

export default useAssignmentLibraryUtils;
